import { MSkeleton } from '@Components/Base';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormikHelpers, useFormik } from 'formik';
import {
  ApprovalHistory,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { PresentationContainer, ViewResolver } from '@Components/index';

import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
  useIsLoadedConent,
  useRequestRefetcher,
} from '@Hooks/index';

import { useGetUsersListByNameQuery } from '@Store/api/userApiSlice';

import { TenderRequestList } from '@Features/MaterialRequest/requests/components/TenderRequestList';
import { modalActions } from '@Store/slices/modalSlice';
import { getModalType } from '@Store/selectors/modalSelector';
import { ViewWidget } from '@Components/ViewWidget';
import { parseSelectedFiltersToOptions } from '@Features/utils/parseSelectedFiltersToOptions';
import { filterOptionsActions } from '@Store/slices/filterOptionsSlice';
import { SortConfig } from '@Features/types/SortConfig';
import { FiltersWidget } from '@Components/FiltersWidget';
import { SearchCriteria } from '@Types/api/common/filters';
import { prepareSearchCriterias } from '@Features/utils/prepareSearchCriterias';
import { NotificationContext } from '@Context/NotificationContext';
import { TABLE_PAGE_DEFAULT } from '@Features/constants';
import {
  getMrForDict,
  getPurchaseClassifications,
  getRequisitionStatusesDictSelector,
  getRFQStatusDict,
  getTenderExtensionTypeDict,
  getTenderForDict,
} from '@Store/selectors/dictionarySelector';
import { isFiltersEmptySelector } from '@Store/selectors/filterOptionsSelector';
import { UpdateTenderModeModal } from '@Features/MaterialRequest/requests/components/UpdateTenderModeModal';
import { TenderExtentionModal } from '@Features/MaterialRequest/requests/components/TenderExtentionModal';
import { VendorsListModal } from '@Features/MaterialRequest/requests/components/VendorsListModal';
import { RequestInitiationModal } from '@Features/MaterialRequest/requests/components/RequestInitiationModal';
import { ApprovalHistory as ApprovalHistoryModal } from '@Components/ApprovalHistory';
import { eventMessageActions } from '@Store/slices/eventMessageSlice';
import { manageRequestActions } from '@Features/store/ManageRequestSlice';
import { TenderDTO } from '@Types/api/form/tenderRequest/TenderDTO';
import { TenderHistoryResponse } from '@Types/api/form';
import { useLazyGetEmpNumberByUserIdQuery } from '@Store/api/dictionaryApiSlice';
import { mapColumns } from './list/MapColumns';
import Filters from './search/Filter';
import {
  filterTypes,
  getFiltersMapper,
  initialValuesFilterForm,
} from './search/filters.utils';
import { FilterForm } from './search/types';
import TenderRequestViewMobileForm from './form/View/mobile/TenderRequestViewMobileForm';
import TenderRequestViewForm from './form/View/TenderRequestViewForm';
import { ActionButton } from '../components/ActionButton';
import {
  useCancelTenderMutation,
  useLazyGetTenderHistoryQuery,
  useRemoveTenderMutation,
} from './store/api/TenderRequestApiSlice';
import { tenderDataSourceMap } from './list/DataSource';

type handleApplyFilterType = (
  values: FilterForm,
  formikHelpers: FormikHelpers<FilterForm>
) => void;

const TenderRequest: React.FC = () => {
  const [cancelTender] = useCancelTenderMutation();
  const [removeTender] = useRemoveTenderMutation();
  const { deleteSuccessNotification, errorNotification } =
    useContext(NotificationContext);
  const { isRequestFormPage } = useOutletContext<{
    isRequestFormPage: boolean;
  }>();
  const isRequestFormPagePrev = useRef(isRequestFormPage);
  const isFiltersEmpty = useAppSelector(isFiltersEmptySelector);
  const statuses = useAppSelector(getRequisitionStatusesDictSelector);
  const prForTypes = useAppSelector(getMrForDict);
  const tenderForTypes = useAppSelector(getTenderForDict);
  const classificationsType = useAppSelector(getPurchaseClassifications);
  const tenderExtensionType = useAppSelector(getTenderExtensionTypeDict);
  const rfqStatusDict = useAppSelector(getRFQStatusDict);
  const dispatch = useAppDispatch();
  const [isApprovalHistoryOpen, setIsApprovalHistoryOpen] = useState(false);
  const [approvalHistoryData, setApprovalHistoryData] = useState<
    TenderHistoryResponse[] | null
  >(null);
  const [getApprovalHistory, { data: approvalHistory }] =
    useLazyGetTenderHistoryQuery();
  const { data: users = [] } = useGetUsersListByNameQuery(null);
  const [tagsValues, setTagsValues] = useState(initialValuesFilterForm);
  const [showTags, setShowTags] = useState(false);
  const [showFiltersError, setShowFiltersError] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [sorting, setSorting] = useState<SortConfig | null>(null);
  const [filters, setFilters] = useState<SearchCriteria[] | undefined>(
    undefined
  );
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [getEmplNumber] = useLazyGetEmpNumberByUserIdQuery();
  const typeModal = useAppSelector(getModalType);
  const [isComparisonSheet, setIsComparisonSheet] = useState(false);
  const [isPreviousPricing, setIsPreviousPricing] = useState(false);

  const closeFilters = (): void => {
    setIsFiltersOpen(false);
  };

  const openFilters = (): void => {
    setIsFiltersOpen(true);
  };

  const handleApplyFilters: handleApplyFilterType = (options): void => {
    const filtersMapper = getFiltersMapper(
      statuses,
      prForTypes,
      tenderForTypes,
      classificationsType,
      tenderExtensionType,
      rfqStatusDict
    );

    const searchCriterias = prepareSearchCriterias(
      options,
      filterTypes,
      filtersMapper
    );

    setFilters(searchCriterias);

    const parsedOptions = parseSelectedFiltersToOptions(options);

    // true if filters was empty and empty after submit
    const isFiltersStillEmpty = isFiltersEmpty && !parsedOptions.length;
    setShowFiltersError(isFiltersStillEmpty);

    if (!isFiltersStillEmpty) {
      closeFilters();
    }

    dispatch(filterOptionsActions.setFilterSettings(searchCriterias));
    dispatch(filterOptionsActions.setFilterOptions(parsedOptions));
    dispatch(eventMessageActions.sendEventType('FILTER'));

    setShowTags(true);
    setTagsValues(options);
  };

  const formik = useFormik({
    initialValues: initialValuesFilterForm,
    enableReinitialize: true,
    onSubmit: handleApplyFilters,
  });

  const clearParams = (): void => {
    setSorting(null);
    setFilters(undefined);
    setSearchValue(undefined);
    setTagsValues(initialValuesFilterForm);
    formik.setValues(initialValuesFilterForm);
  };

  const {
    currentPage,
    fetchRequest,
    handleLoadMore,
    onChangeCurrentPage,
    isSuccess,
    isLoading,
    listRequests,
    requestsDataList,
  } = useRequestRefetcher({
    clearParams,
    filters,
    isRequestFormPage,
    isRequestFormPagePrev,
    requestType: 'Tender',
    searchValue,
    sorting,
  });

  const isContentLoaded = useIsLoadedConent(isSuccess);

  useEffect(() => {
    isRequestFormPagePrev.current = isRequestFormPage;
  }, [isRequestFormPage]);

  useEffect(() => {
    const comparisonSheet = pathname.includes('comparison-sheet');

    setIsComparisonSheet(comparisonSheet);
  }, [pathname, setIsComparisonSheet]);

  useEffect(() => {
    const previousPricing = pathname.includes('previous-pricing');

    setIsPreviousPricing(previousPricing);
  }, [pathname, setIsPreviousPricing]);

  const isRequestInitiationOpen = useMemo(
    () => typeModal === 'InitiateRFQ',
    [typeModal]
  );

  const isViewWidgetOpen = useMemo(
    () => typeModal === 'TenderView',
    [typeModal]
  );

  const isVendorListOpen = useMemo(
    () => typeModal === 'TenderVendorList',
    [typeModal]
  );

  const isUpdateTenderModeModal = useMemo(
    () => typeModal === 'UpdateTenderMode',
    [typeModal]
  );

  const isUpdateTenderExtensionModal = useMemo(
    () => typeModal === 'UpdateTenderExtension',
    [typeModal]
  );

  const dataSource = useMemo(
    () =>
      tenderDataSourceMap(
        (listRequests as RequestListSearchResponse<TenderDTO>)?.content
      ),
    [listRequests]
  );

  useEffect(() => {
    dispatch(manageRequestActions.setActionComponent(<ActionButton />));
  }, [dispatch]);

  const handleSort = useCallback(
    (sortConfig: SortConfig | null) => {
      fetchRequest(TABLE_PAGE_DEFAULT, sortConfig);

      onChangeCurrentPage(TABLE_PAGE_DEFAULT);
      setSorting(sortConfig);
    },
    [fetchRequest]
  );

  const handleDeleteRequest = (): void => {
    deleteSuccessNotification('no endpoint');
  };

  const handleChangePage = useCallback((page: number): void => {
    onChangeCurrentPage(page);
    dispatch(eventMessageActions.sendEventType('REFETCH'));
  }, []);

  useEffect(() => {
    dispatch(filterOptionsActions.setFilterOptions(null));
  }, [dispatch]);

  const handleCloseViewWidget = (): void => {
    dispatch(
      modalActions.setStatusModal({ status: false, key: '', type: undefined })
    );
  };

  const handleComparisonSheet = useCallback(
    (recordKey: string, taskID?: string): void => {
      if (taskID != null) {
        navigate(`comparison-sheet/${recordKey}/${taskID}`);
      }
    },
    [navigate]
  );

  const handlePreviousPricing = useCallback((recordKey: string): void => {
    navigate(`${recordKey}/previous-pricing`);
  }, []);

  const handleSearch = useCallback((value: string | null) => {
    setSearchValue(value || undefined);
    dispatch(eventMessageActions.sendEventType('SEARCH'));
  }, []);

  const getHistoryWithEmploeeNumber = useCallback(
    async (
      historyRecord: TenderHistoryResponse
    ): Promise<TenderHistoryResponse> => {
      const empNumber = await getEmplNumber(
        Number(historyRecord.actionBy)
      ).unwrap();
      return { ...historyRecord, actionBy: empNumber };
    },
    [getEmplNumber]
  );

  useLayoutEffect(() => {
    if (approvalHistory?.length) {
      const usersPromises: Promise<TenderHistoryResponse>[] = [];
      if (Array.isArray(approvalHistory) && approvalHistory.length) {
        approvalHistory.forEach((rec) => {
          usersPromises.push(getHistoryWithEmploeeNumber(rec));
        });
      }
      Promise.all(usersPromises).then((emplsData) => {
        setIsApprovalHistoryOpen(true);
        setApprovalHistoryData(emplsData);
      });
    }
  }, [approvalHistory, getHistoryWithEmploeeNumber]);

  const handleApprovalHistory = useCallback(
    (requestNo: string) => {
      getApprovalHistory(Number(requestNo))
        .unwrap()
        .catch(({ data }) => {
          errorNotification(data);
        });
    },
    [getApprovalHistory, errorNotification]
  );

  const tagsFormik = {
    ...formik,
    values: tagsValues,
  };

  const handleCancelTender = useCallback(
    (recordKey: string): void => {
      cancelTender(Number(recordKey));
      fetchRequest();
    },
    [cancelTender, fetchRequest]
  );

  const handleDeleteTender = useCallback(
    (recordKey: string): void => {
      removeTender(Number(recordKey))
        .unwrap()
        .then(() => {
          deleteSuccessNotification(recordKey);
          fetchRequest();
        })
        .catch(({ data }) => {
          errorNotification(data);
        });
    },
    [removeTender, deleteSuccessNotification, fetchRequest, errorNotification]
  );

  return (
    <PresentationContainer>
      {(isComparisonSheet || isPreviousPricing) && <Outlet />}
      <FiltersWidget
        onSearchSubmit={handleSearch}
        isOpen={isFiltersOpen}
        onClose={closeFilters}
        onOpen={openFilters}
        formik={tagsFormik}
        showTags={showTags}
      >
        <Filters showError={showFiltersError} formik={formik} />
      </FiltersWidget>
      {isContentLoaded ? (
        <TenderRequestList
          onLoadMore={handleLoadMore}
          onComparison={handleComparisonSheet}
          onPrevious={handlePreviousPricing}
          onApprovalHistory={handleApprovalHistory}
          onCancel={handleCancelTender}
          onDelete={handleDeleteTender}
          onChangePage={handleChangePage}
          currentPage={currentPage}
          onSort={handleSort}
          listRequests={listRequests as RequestListSearchResponse<TenderDTO>}
          requestsDataList={requestsDataList as Array<TenderDTO>}
          mapColumns={mapColumns}
          dataSource={dataSource}
          requestType="Tender"
          isFiltersApplied={Boolean(filters)}
          isSearchApplied={Boolean(searchValue)}
          loading={isLoading}
        />
      ) : (
        <MSkeleton />
      )}

      <ViewWidget
        title="Tender Details"
        isOpen={isViewWidgetOpen}
        onClose={handleCloseViewWidget}
      >
        <ViewResolver
          desktop={<TenderRequestViewForm />}
          mobile={
            <TenderRequestViewMobileForm
              onCancel={() => null}
              onDelete={handleDeleteRequest}
            />
          }
        />
      </ViewWidget>
      <UpdateTenderModeModal
        isOpen={isUpdateTenderModeModal}
        onCancel={handleCloseViewWidget}
      />
      <VendorsListModal
        isOpen={isVendorListOpen}
        onCancel={handleCloseViewWidget}
      />
      <RequestInitiationModal
        isOpen={isRequestInitiationOpen}
        onCancel={handleCloseViewWidget}
        onSuccess={fetchRequest}
      />
      <TenderExtentionModal
        isOpen={isUpdateTenderExtensionModal}
        onSuccess={handleCloseViewWidget}
        onCancel={handleCloseViewWidget}
      />
      <ApprovalHistoryModal
        isOpen={isApprovalHistoryOpen}
        onClose={() => {
          setIsApprovalHistoryOpen(false);
        }}
        data={
          approvalHistoryData?.map(
            (hisRec) =>
              ({
                actId: hisRec.id,
                requestId: hisRec.tenderID,
                action: hisRec.action,
                actionByEmpNumber: String(hisRec.actionBy),
                actionDate: hisRec.actionDate,
                comments: hisRec.comments,
              }) as ApprovalHistory
          ) || null
        }
        users={users}
      />
    </PresentationContainer>
  );
};

export default TenderRequest;
